import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import { userModel } from '@/models/auth/userModel'
import { logger } from '@/plugins/logger'
import { getTrackingHeaders } from '@/plugins/tracker'

const TOKEN_KEY = process.env.VUE_APP_AUTH_COOKIE_NAME || 'auth_token'

function getCookie (name) {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(';').shift()
  return null
}

export const authModel = {
  namespaced: true,
  state () {
    return {
      authToken: null,
      overrideToken: null,
    }
  },
  getters: {
    authToken (state) {
      return state.overrideToken || state.authToken
    },
    resetToken (state, getters, rootState, rootGetters) {
      return rootGetters['routeQuery'].t
    },
    isLoggedIn (state, getters) {
      let token = getters.authToken
      if (!token) {
        return false
      }
      let expirationDate = new Date(0)
      expirationDate.setUTCSeconds(jwtDecode(token).exp)

      if (expirationDate < new Date()) {
        return false
      }

      return true
    },
    companyUser (state, getters, rootState, rootGetters) {
      return rootGetters['auth/user/companyUser']
    },
    permissions (state, getters, rootState, rootGetters) {
      return rootGetters['auth/user/permissions']
    },
    loggedInUser (state, getters, rootState, rootGetters) {
      return rootGetters['auth/user/loggedInUser']
    },
    defaultCompanyId (state, getters, rootState, rootGetters) {
      return rootGetters['auth/user/defaultCompanyId']
    },
  },
  mutations: {
    setAuthToken (state, token) {
      state.authToken = token
    },
    setOverrideToken (state, token) {
      state.overrideToken = token
    },
    clearOverrideToken (state) {
      state.overrideToken = null
    },
  },
  actions: {
    logout (context) {
      context.commit('setAuthToken', null)
      context.commit('clearOverrideToken')
      context.commit('user/setUser', null)
    },
    async syncOverrideFromUrlParams (context) {
      const params = new URLSearchParams(window.location.search)
      const flagsOn = params.getAll('feature_flag')
      const flagsOff = params.getAll('feature_flag_off')
      const roleId = params.get('previewRole')

      if (flagsOn.length === 0 && flagsOff.length === 0 && !roleId) return

      const overrides = {}
      for (const f of flagsOn) overrides[f] = true
      for (const f of flagsOff) overrides[f] = false

      const data = { feature_flag_overrides: overrides, }
      if (roleId) {
        data.impersonate_role_id = parseInt(roleId, 10)
      }

      try {
        let token = context.state.authToken
        let response = await axios.post(
          `${process.env.VUE_APP_ADMIN_API_URL}/login/refresh`,
          data,
          { headers: { authorization: token, ...getTrackingHeaders(), }, }
        )
        if (response.data?.valid && response.data?.token) {
          context.commit('setOverrideToken', response.data.token)
        }
      } catch (e) {
        console.warn('[devtools] Failed to sync override token from URL params:', e)
      }
    },
    async initialize (context, companyId) {
      logger.info('auth-initialize-start', {
        hasExistingUser: context.getters.loggedInUser !== null,
      })
      if (context.getters.loggedInUser !== null) {
        // Flags resolve per company, so moving to another company refetches them.
        if (companyId && `${companyId}` !== `${context.getters['user/featureFlagsCompanyId']}`) {
          await context.dispatch('user/loadUser', companyId)
        }
        logger.info('auth-initialize-skip-existing-user')
        return
      }
      let persistedToken = getCookie(TOKEN_KEY)
      logger.info('auth-initialize-cookie-read', {
        hasToken: persistedToken !== null,
        tokenLength: persistedToken?.length ?? 0,
      })
      if (persistedToken === null) {
        logger.warn('auth-initialize-no-token')
        return
      }
      context.commit('setAuthToken', persistedToken)
      await context.dispatch('syncOverrideFromUrlParams')
      await context.dispatch('user/loadUser', companyId)
      logger.info('auth-initialize-complete', {
        isLoggedIn: context.getters.isLoggedIn,
      })
      return
    },
  },
  modules: {
    user: userModel,
  },
}
